import {
  PERCEPTION_MODELS,
  PERCEPTION_MODEL_TRANSLATION,
} from 'constants/routePaths';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  PerceptionModelJobsQuery,
  ProjectNormSetByIdQuery,
  type PerceptionModelJobOption,
  type ProjectNormSetRow,
} from '@graphql/queries/PerceptionModelQueries';
import { ArrowBack } from '@mui/icons-material';
import { Alert, Button, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';

import { normSetMembers } from './normSetUtils';
import PerceptionModelError from './PerceptionModelError';
import TranslationBuilder from './TranslationBuilder';
import { PageHeader, PagePaper, PageTitle } from '../../styles/themeComponents';

/**
 * Attach a translation to a saved norm set.
 *
 * The norm set arrives as a route parameter rather than a field, because it is
 * the parent: the model, and so the levels a demographic may name, come from it
 * and cannot be chosen here.
 *
 * Read scoped by producerId, the same condition the detail page uses. There is
 * no row level security on this table, so that term is the only thing stopping
 * a typed id from another workspace resolving — and since the norm set has to
 * resolve before the builder renders, it also gates the write.
 */

type Props = { normSetId: number };

const TranslationPage: React.FC<Props> = ({ normSetId }) => {
  const history = useHistory();
  const producerId = useSelector((state) => selectWorkspaceProducerId(state));

  const { data, loading, error } = useQuery(ProjectNormSetByIdQuery, {
    variables: { id: normSetId, producerId },
    skip: !producerId,
  });
  const row: ProjectNormSetRow | null =
    data?.allPerceptionModelProjects?.nodes?.[0] ?? null;

  // Needed for demo_levels: the picker can only offer levels this model saw,
  // and nothing downstream rejects one it did not.
  const { data: jobData, loading: jobsLoading } = useQuery(
    PerceptionModelJobsQuery,
    { skip: !row },
  );
  const job = (jobData?.allPerceptionModelJobs?.nodes ?? []).find(
    (j: PerceptionModelJobOption) => j.id === row?.perceptionModelJobId,
  );

  const members = React.useMemo(
    () => normSetMembers(row?.normSet ?? null).map((m) => m.product),
    [row],
  );

  const backToNormSet = () => history.push(`${PERCEPTION_MODELS}/${normSetId}`);

  // Saving an audience is never the last step — the products still have to be
  // chosen and the report triggered, and both live on the translation itself.
  // Landing back on the norm set made the analyst find the row they had just
  // made before they could carry on.
  const openCreated = (createdId: number) =>
    createdId
      ? history.push(
          PERCEPTION_MODEL_TRANSLATION.replace(
            ':normSetId',
            String(normSetId),
          ).replace(':translationId', String(createdId)),
        )
      : backToNormSet();

  if (loading || !producerId) return <PagePaper>Loading norm set…</PagePaper>;

  if (error) {
    return (
      <PagePaper>
        <PerceptionModelError error={error} />
      </PagePaper>
    );
  }

  if (!row) {
    return (
      <PagePaper>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => history.push(PERCEPTION_MODELS)}
            >
              Back
            </Button>
          }
        >
          No norm set #{normSetId}.
        </Alert>
      </PagePaper>
    );
  }

  return (
    <PagePaper>
      {/* One level up is the norm set, not the list: a translation is its
          child, and skipping a level is what made the depth invisible. */}
      <Button
        size="small"
        startIcon={<ArrowBack fontSize="small" />}
        onClick={() => history.push(`${PERCEPTION_MODELS}/${normSetId}`)}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        {row.name}
      </Button>

      <PageHeader>Translation · on {row.name}</PageHeader>
      <PageTitle>New translation</PageTitle>

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Job {row.perceptionModelJobId} · {members.length} products
      </Typography>

      <Alert severity="info" sx={{ mt: 2 }}>
        Rendered against job {row.perceptionModelJobId}, the model this norm set
        was built on, rather than the current one. That is what keeps a
        translation comparable with the norm set it came from.
      </Alert>

      {members.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This norm set has no products, so there is nothing to translate.
        </Alert>
      )}

      {/* The picker opens on defaults narrowed to this model's levels, so it
          waits for them rather than re-seeding underneath a half-filled form. */}
      {jobsLoading ? (
        <Typography sx={{ mt: 2 }}>Loading model…</Typography>
      ) : (
        <TranslationBuilder
          projectId={row.id}
          job={job}
          members={members}
          onDone={openCreated}
          onCancel={backToNormSet}
        />
      )}
    </PagePaper>
  );
};

export default TranslationPage;
